// Typed client for /api/console/*.
import { ApiError } from '../api/client.ts';
import type { MenuExtras, ShopStatus } from '../api/types.ts';

export interface ConsoleShop {
  id: string;
  slug: string;
  name: string;
  status: ShopStatus;
  serviceUntil: string;
  hasPaid: boolean;
  manualSuspend: boolean;
  closedAt: string | null;
  lineConnected: boolean;
  lastWebhookAt: string | null;
  health: { level?: 'green' | 'yellow' | 'red'; warnings?: string[]; token?: boolean; webhook?: boolean; quotaPct?: number | null; checkedAt?: string };
  bookingsThisMonth: number;
  messagesThisMonth: number;
  createdAt: string;
}

export interface ConsoleShopDetail extends ConsoleShop {
  phone: string | null;
  platformNote: string | null;
  webhookUrl: string;
  liffEndpoint: string;
  line: { channelId: string | null; channelSecretHint: string | null; accessTokenHint: string | null; loginChannelId: string | null; liffId: string | null; basicId: string | null };
  payments: { amount: number; months: number; note: string | null; recordedBy: string; paidAt: string }[];
  recentErrors: { source: string; message: string; createdAt: string }[];
}

export interface CheckItem { key: string; ok: boolean; detail?: string }
export interface ErrorRow { id: string; shopSlug: string | null; source: string; message: string; createdAt: string }

export function createConsoleApi(o: { baseUrl: string; getToken: () => Promise<string | null>; fetchImpl?: typeof fetch }) {
  const doFetch = o.fetchImpl ?? ((...a: Parameters<typeof fetch>) => fetch(...a));
  const base = `${o.baseUrl.replace(/\/+$/, '')}/api/console`;
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await o.getToken();
    let res: Response;
    try {
      res = await doFetch(base + path, {
        method,
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch {
      throw new ApiError(0, 'NETWORK', 'เชื่อมต่อไม่ได้ กรุณาลองใหม่');
    }
    const data = await res.json().catch(() => null) as { error?: { code: string; message: string; field?: string; details?: Record<string, unknown> } } | null;
    if (!res.ok) {
      const e = data?.error;
      throw new ApiError(res.status, e?.code ?? 'INTERNAL', e?.message ?? 'ระบบขัดข้อง กรุณาลองใหม่', e?.field, e?.details);
    }
    return data as T;
  }
  const enc = encodeURIComponent;
  return {
    me: () => call<{ email: string; isAdmin: boolean; aal: string }>('GET', '/me'),
    shops: () => call<ConsoleShop[]>('GET', '/shops'),
    createShop: (b: { name: string; slug: string; phone?: string }) => call<ConsoleShopDetail>('POST', '/shops', b),
    shop: (id: string) => call<ConsoleShopDetail>('GET', `/shops/${enc(id)}`),
    updateShop: (id: string, b: { name?: string; phone?: string; platformNote?: string; manualSuspend?: boolean; close?: boolean; reopen?: boolean }) =>
      call<ConsoleShopDetail>('PATCH', `/shops/${enc(id)}`, b),
    setLine: (id: string, b: { channelId: string; channelSecret: string; accessToken: string; loginChannelId: string; liffId: string }) =>
      call<{ ok: true }>('PUT', `/shops/${enc(id)}/line`, b),
    check: (id: string) => call<CheckItem[]>('POST', `/shops/${enc(id)}/check`, {}),
    ownerInvite: (id: string) => call<{ url: string | null; expiresAt: string }>('POST', `/shops/${enc(id)}/owner-invite`, {}),
    recordPayment: (id: string, b: { amount: number; months: number; note?: string }) =>
      call<{ serviceUntil: string; status: ShopStatus }>('POST', `/shops/${enc(id)}/payments`, b),
    richMenuImage: (id: string, imageBase64: string, contentType: string, extras?: MenuExtras) =>
      call<{ ok: true }>('POST', `/shops/${enc(id)}/rich-menu-image`, { imageBase64, contentType, ...extras }),
    errors: () => call<ErrorRow[]>('GET', '/errors'),
  };
}

export type ConsoleApi = ReturnType<typeof createConsoleApi>;
